<template>
  <div>
    <div class="container-fluid">
      <div class="row">
        <div class="col-sm-12">
          <div class="card">
            <div class="card-body pt-3 pb-3">
              <div class="row align-items-center">
                <div class="col-md-3">
                  <h5 class="mb-0">Scan Order</h5>
                </div>
                <div class="col-md-9">
                  <div class="input-group">
                    <span class="input-group-text"><vue-feather type="search"></vue-feather></span>
                    <input 
                      class="form-control" 
                      id="orderCode" 
                      type="text" 
                      placeholder="Order Code / Tracking Number..." 
                      v-model="orderCode"
                      @keyup.enter="handleScan"
                      ref="scanInput"
                      :disabled="loading"
                    >
                    <button class="btn btn-primary" type="button" @click="handleScan" :disabled="loading">
                      <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Scan
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Order Detail Content (Adapted from orderDetailModal) -->
      <div class="row mt-2" v-if="order">
        <div class="col-sm-12">
          <div class="card shadow-sm border-0">
            <div class="card-body p-4">
              <!-- Status Header -->
              <div class="card mb-4 border-0 bg-light-primary shadow-sm">
                <div class="card-body py-3 px-4">
                  <div class="row align-items-center text-center text-md-start">
                    <div class="col-md-4 mb-2 mb-md-0">
                      <div class="text-muted mb-1" style="font-size: 13px;">Mã đơn hàng</div>
                      <div class="fw-bold text-primary h5 m-0">{{ order.code }}</div>
                    </div>
                    <div class="col-md-3 mb-2 mb-md-0 border-start-md">
                      <div class="text-muted mb-1" style="font-size: 13px;">Tracking Number</div>
                      <div class="fw-bold h5 m-0">{{ order.tracking_number || 'Chưa có' }}</div>
                    </div>
                    <div class="col-md-3 text-md-end border-start-md">
                      <div class="text-muted mb-1" style="font-size: 13px;">Trạng thái</div>
                      <div class="status-badge-custom py-2 px-3 fw-bold shadow-sm" :class="getStatusClass(order.status_id)" style="font-size: 14px;">
                        {{ getStatusName(order.status_id) }}
                      </div>
                    </div>
                    <div class="col-md-2 text-md-end">
                      <router-link :to="{ name: 'orderDetails', params: { id: order.id } }" class="btn btn-sm btn-outline-primary">
                        <i class="fa fa-edit me-1"></i> Edit
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row">
                <!-- Left Column -->
                <div class="col-md-6">
                  <!-- Creator Info -->
                  <div class="section mb-4 p-3 border rounded bg-white shadow-sm">
                    <h6 class="border-bottom pb-2 text-dark fw-bold mb-3">
                      <i class="fa fa-user-circle me-2 text-primary"></i>Người tạo
                    </h6>
                    <div class="row g-3">
                      <div class="col-12"><strong>Tên:</strong> {{ order.customer?.name || 'N/A' }}</div>
                      <div class="col-12"><strong>Điện thoại:</strong> {{ order.customer?.phone || 'N/A' }}</div>
                      <div class="col-12"><strong>Email:</strong> {{ order.customer?.email || 'N/A' }}</div>
                    </div>
                  </div>

                  <!-- Package Info -->
                  <div class="section mb-4 p-3 border rounded bg-white shadow-sm">
                    <h6 class="border-bottom pb-2 text-dark fw-bold mb-3">
                      <i class="fa fa-cube me-2 text-primary"></i>Thông tin kiện hàng
                    </h6>
                    <div class="row g-3">
                      <div class="col-6"><strong>Tên kiện hàng:</strong> {{ order.package_name }}</div>
                      <div class="col-6"><strong>Kho nhận:</strong> {{ order.warehouse_id || 'N/A' }}</div>
                      <div class="col-6"><strong>Dài (cm):</strong> {{ order.length }}</div>
                      <div class="col-6"><strong>Rộng (cm):</strong> {{ order.width }}</div>
                      <div class="col-6"><strong>Cao (cm):</strong> {{ order.height }}</div>
                      <div class="col-6"><strong>Cân nặng (g):</strong> {{ order.weight }}</div>
                      <div class="col-12"><strong>Dim weight (g):</strong> <span class="text-primary fw-bold">{{ calculateDimWeight(order) }}</span></div>
                      <div class="col-12"><strong>Ghi chú từ khách:</strong> {{ order.customer_note || '--' }}</div>
                    </div>
                  </div>
                </div>

                <!-- Right Column -->
                <div class="col-md-6">
                  <!-- Shipping Info -->
                  <div class="section mb-4 p-3 border rounded bg-white shadow-sm h-100">
                    <h6 class="border-bottom pb-2 text-dark fw-bold mb-3">
                      <i class="fa fa-map-marker me-2 text-primary"></i>Thông tin gửi & nhận hàng
                    </h6>
                    <div class="row g-3">
                      <div class="col-12">
                        <label class="d-block text-muted mb-0" style="font-size: 13px;">Tên đơn hàng</label>
                        <span class="fw-bold text-dark">{{ order.name }}</span>
                      </div>
                      <div class="col-6">
                        <label class="d-block text-muted mb-0" style="font-size: 13px;">Dịch vụ</label>
                        <span>{{ getServiceName(order.service_id) }}</span>
                      </div>
                      <div class="col-6">
                        <label class="d-block text-muted mb-0" style="font-size: 13px;">Giá trị đơn (USD)</label>
                        <span class="text-success fw-bold">${{ Number(order.price || 0).toLocaleString() }}</span>
                      </div>
                      <div class="col-12">
                        <label class="d-block text-muted mb-0" style="font-size: 13px;">Địa chỉ hồi trả</label>
                        <p class="mb-0">{{ order.return_address || '--' }}</p>
                      </div>
                    </div>

                    <div class="receiver-box mt-3 pt-3 border-top">
                      <div class="fw-bold text-dark mb-2">Người nhận</div>
                      <div class="row g-2">
                        <div class="col-12"><strong>Họ tên:</strong> {{ order.receiver_name }}</div>
                        <div class="col-6"><strong>Điện thoại:</strong> {{ order.receiver_phone }}</div>
                        <div class="col-6"><strong>Email:</strong> {{ order.receiver_email }}</div>
                        <div class="col-12"><strong>Địa chỉ:</strong> {{ order.receiver_address }}</div>
                        <div class="col-6"><strong>Thành phố:</strong> {{ order.receiver_city }}</div>
                        <div class="col-6"><strong>Bang:</strong> {{ order.receiver_state }}</div>
                        <div class="col-6"><strong>Quốc gia:</strong> {{ order.receiver_country?.display_name || order.receiver_country_id }}</div>
                        <div class="col-6"><strong>Mã bưu điện:</strong> {{ order.receiver_post_code }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Items List -->
              <div class="section mb-4 p-3 border rounded bg-white shadow-sm">
                <h6 class="border-bottom pb-2 text-dark fw-bold mb-3">
                  <i class="fa fa-list me-2 text-primary"></i>Danh sách hàng
                </h6>
                <div class="table-responsive">
                  <table class="table table-hover table-bordered mb-0" style="font-size: 14px;">
                    <thead class="bg-light">
                      <tr>
                        <th class="ps-3 py-2">Tên</th>
                        <th class="py-2">HSCode</th>
                        <th class="py-2">Mã YunExpress</th>
                        <th class="py-2">Giá</th>
                        <th class="text-center py-2">SL</th>
                        <th class="py-2">Mô tả</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, idx) in order.order_items" :key="idx">
                        <td class="ps-3 py-2">{{ item.name }}</td>
                        <td class="py-2">{{ item.hscode }}</td>
                        <td class="py-2 text-break" style="max-width: 240px;">
                          <div v-if="item.mp_id"><span class="text-muted fw-semibold">MPId:</span> {{ item.mp_id }}</div>
                          <div v-if="item.nsp_id"><span class="text-muted fw-semibold">NSPId:</span> {{ item.nsp_id }}</div>
                          <div v-if="item.sp_id"><span class="text-muted fw-semibold">SPId:</span> {{ item.sp_id }}</div>
                          <span v-if="!item.mp_id && !item.nsp_id && !item.sp_id">—</span>
                        </td>
                        <td class="text-success py-2">${{ Number(item.price || 0).toLocaleString() }}</td>
                        <td class="text-center py-2">{{ item.quantity }}</td>
                        <td class="py-2">{{ item.description }}</td>
                      </tr>
                      <tr v-if="!order.order_items?.length">
                        <td colspan="6" class="text-center text-muted py-3">Không có sản phẩm</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Fee Footer -->
              <div class="section mt-4 p-4 bg-primary text-white rounded-3 shadow">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="d-flex flex-column">
                    <span class="small opacity-75">TỔNG CỘNG</span>
                    <small class="opacity-50">Đã bao gồm các loại thuế phí</small>
                  </div>
                  <span class="h4 m-0 fw-bold">{{ Number(order.total_order_fee || 0).toLocaleString() }} đ</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-2" v-if="!order && lastSearchedCode && !loading">
        <div class="col-sm-12">
          <div class="alert alert-warning">
            No order found with code: {{ lastSearchedCode }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useOrders } from "@/composables/useOrders";
import { useService } from "@/composables/useService";
import { useStatus } from "@/composables/useStatus";
import { createToaster } from "@meforma/vue-toaster";

const orderCode = ref('');
const order = ref(null);
const loading = ref(false);
const lastSearchedCode = ref('');
const scanInput = ref(null);
const services = ref([]);
const statuses = ref([]);

const { fetchOrdersApi, changeStatusApi, getTrackingDataApi } = useOrders();
const { getServicesApi } = useService();
const { getStatusesApi } = useStatus();
const toaster = createToaster({
  position: "top-right",
  duration: 2000,
});

onMounted(async () => {
  if (scanInput.value) {
    scanInput.value.focus();
  }
  
  // Fetch services and statuses for display mapping
  try {
    const resS = await getServicesApi({ limit: -1 });
    const resSt = await getStatusesApi({ limit: -1 });
    if (resS.success) services.value = resS.data.data;
    if (resSt.success) statuses.value = resSt.data.data;
  } catch (err) {
    console.error("Error fetching lookup data:", err);
  }
});

const getServiceName = (id) => {
  const service = services.value.find(s => s.id === id);
  return service ? service.name : 'Unknown';
};

const getStatusName = (id) => {
  const status = statuses.value.find(s => s.id === id);
  return status ? status.display_name : 'Unknown';
};

const calculateDimWeight = (order) => {
  if (!order.length || !order.width || !order.height) return 0;
  return ((order.length * order.width * order.height) / 5).toFixed(1);
};

const getStatusClass = (id) => {
  const map = {
    10: 'status-new',
    11: 'status-conf',
    12: 'status-wh',
    14: 'status-transmit',
    15: 'status-done',
    16: 'status-return',
    17: 'status-reserve',
    18: 'status-cancel',
    19: 'status-update',
    20: 'status-loss',
    21: 'status-reimburse',
  };
  return map[id] || 'status-default';
};

const handleScan = async () => {
  const code = orderCode.value.trim();
  if (!code) {
    toaster.error('Please enter an Order Code or Tracking Number');
    return;
  }

  loading.value = true;
  lastSearchedCode.value = ''; // Reset
  order.value = null;

  try {
    const params = {
      page: 1,
      limit: 10,
      orderBy: 'id',
      sortedBy: 'desc',
      searchJoin: 'OR',
      include: 'customer,order_items,fees',
      searchFields: 'code:=;tracking_number:=',
      search: `code:${code};tracking_number:${code}`
    };

    const res = await fetchOrdersApi(params);

    if (res.success && res.data && res.data.data && res.data.data.length > 0) {
      const foundOrder = res.data.data[0];
      
      // Call change-status API as requested
      const statusRes = await changeStatusApi({
        ids: [foundOrder.id],
        status: "order-in-warehouse-wait-package"
      });

      if (statusRes.success) {
        // Re-fetch to get the updated status and details AND get tracking data concurrently
        const [updatedRes, trackingRes] = await Promise.all([
          fetchOrdersApi({
            page: 1,
            limit: 1,
            include: 'customer,order_items,fees',
            searchFields: 'id:=',
            search: `id:${foundOrder.id}`
          }),
          getTrackingDataApi(foundOrder.id)
        ]);

        if (updatedRes.success && updatedRes.data && updatedRes.data.data && updatedRes.data.data.length > 0) {
          order.value = updatedRes.data.data[0];
        } else {
          order.value = foundOrder; // Fallback
        }

        toaster.success('Order found and status updated: ' + code);
        
        // Optional: clear input for next scan
        orderCode.value = '';
        lastSearchedCode.value = '';
      } else {
        // If status update fails, show the error from API
        const errorMsg = statusRes.message || 'Error updating status';
        toaster.error(errorMsg);
        // Important: don't set lastSearchedCode here so the yellow alert doesn't show
      }
    } else {
      // Order not found in search
      toaster.error('No order found with code: ' + code);
      lastSearchedCode.value = code; // This triggers the yellow alert
    }
  } catch (err) {
    console.error('Scan error:', err);
    toaster.error('An error occurred while scanning');
  } finally {
    loading.value = false;
    setTimeout(() => {
      if (scanInput.value) scanInput.value.focus();
    }, 100);
  }
};
</script>

<style scoped>
.input-group-text {
  background-color: #f8f9fa;
}

.bg-light-primary {
  background-color: #f0f7ff;
}

.status-badge-custom {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 50px;
  font-size: 11px;
  font-weight: 600;
}

.border-start-md {
  border-left: 1px solid #dee2e6;
}

@media (max-width: 768px) {
  .border-start-md {
    border-left: none;
  }
}

/* Color Mapping for Statuses */
.status-new { background-color: #e0f2fe; color: #0369a1; }
.status-conf { background-color: #e0e7ff; color: #4338ca; }
.status-wh { background-color: #ffedd5; color: #c2410c; }
.status-transmit { background-color: #dbeafe; color: #1d4ed8; }
.status-done { background-color: #d1fae5; color: #047857; }
.status-return { background-color: #f3e8ff; color: #7e22ce; }
.status-reserve { background-color: #ccfbf1; color: #0f766e; }
.status-cancel { background-color: #fee2e2; color: #b91c1c; }
.status-update { background-color: #fce7f3; color: #be185d; }
.status-loss { background-color: #111827; color: #ffffff; }
.status-reimburse { background-color: #fef9c3; color: #a16207; }
.status-default { background-color: #f9fafb; color: #6b7280; }
</style>
